import { Icon, IconButton, Menu } from '@elipssolution/harfang';
import { mdiCog } from '@mdi/js';
import { MenuItem, Paper, Stack, Typography, styled } from '@mui/material';
import clsx from 'clsx';
import { MouseEvent, useCallback, useMemo, useState } from 'react';

import { WidgetWithKeyType } from '../../../../types/widget';
import { WidgetEnum } from '../../../../utils/widgets';
import { getWidgetTemplateByType } from '../../../widgets/widgets';

const StyledDashboardItem = styled(Paper)(({ theme: { palette, shape } }) => ({
	border: `1px solid ${palette.divider}`,
	borderRadius: shape.borderRadius,
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	width: '100%',
	height: '100%',

	'&.hasOverflow > div:nth-of-type(2)': {
		overflow: 'auto',
	},
}));

type WidgetContextMenuProps = {
	onEdit?: () => void;
	onRemove?: () => void;
};

const WidgetContextMenu = ({ onEdit, onRemove }: WidgetContextMenuProps) => {
	const [localWidgetMenuAnchorEl, setWidgetMenuAnchorEl] = useState<HTMLButtonElement>();

	const openMenu = ({ currentTarget }: MouseEvent<HTMLButtonElement>) => setWidgetMenuAnchorEl(currentTarget);

	const closeMenu = useCallback(() => setWidgetMenuAnchorEl(undefined), []);

	const handleEdit = () => {
		closeMenu();
		onEdit?.();
	};

	const handleRemove = () => {
		closeMenu();
		onRemove?.();
	};

	return (
		<>
			<Stack alignItems="center" padding={1}>
				<IconButton onClick={openMenu} size="small">
					<Icon path={mdiCog} size="small" />
				</IconButton>
			</Stack>

			<Menu
				anchorEl={localWidgetMenuAnchorEl}
				open={!!localWidgetMenuAnchorEl}
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				onClose={closeMenu}
			>
				{onEdit && <MenuItem onClick={handleEdit}>Modifier</MenuItem>}
				{onRemove && <MenuItem onClick={handleRemove}>Supprimer</MenuItem>}
			</Menu>
		</>
	);
};

type DesktopDashboardItemProps = {
	widgetWithKey: WidgetWithKeyType;
	readOnly: boolean;
	onEdit?: (widget: WidgetWithKeyType) => void;
	onRemove?: (widget: WidgetWithKeyType) => void;
};

const DesktopDashboardItem = ({ widgetWithKey, readOnly, onEdit, onRemove }: DesktopDashboardItemProps) => {
	const { widget } = widgetWithKey;
	const { name, type, configuration } = widget;
	const { component: WidgetComponent } = getWidgetTemplateByType(type);

	const parsedConfiguration = useMemo(
		() => (configuration ? (JSON.parse(configuration) as object) : undefined),
		[configuration],
	);

	return (
		<StyledDashboardItem className={clsx({ hasOverflow: type === WidgetEnum.HTML })}>
			<Stack direction="row">
				<Typography flex={1} padding={1} variant="h6" noWrap>
					{name}
				</Typography>

				{(onEdit || onRemove) && (
					<WidgetContextMenu onEdit={() => onEdit?.(widgetWithKey)} onRemove={() => onRemove?.(widgetWithKey)} />
				)}
			</Stack>

			<Stack flex={1} padding={2} paddingTop={0}>
				<WidgetComponent widgetWithKey={widgetWithKey} configuration={parsedConfiguration} readOnly={readOnly} />
			</Stack>
		</StyledDashboardItem>
	);
};

export default DesktopDashboardItem;
